import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { InputError } from '../InputError/InputError';
import styles from './input.module.scss';

export interface IInputProps {
    autoFocus?: boolean;
    name: string;
    type?: string;
    value?: string;
    mask?: string;
    maskChar?: string;
    disabled?: boolean;
    placeholder?: string;
    label?: string;
    maxLength?: number;
    autoComplete?: string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    className?: string;
    onChange?: (event: React.FormEvent) => void;
    onBlur?: (event: React.FormEvent) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
}

export interface IInputHOCProps extends WrappedComponentProps {
    autofocus?: boolean;
    name: string;
    type?: string;
    value?: string;
    disabled?: boolean;
    mask?: string;
    maskChar?: string;
    numeric?: boolean;
    required?: boolean;
    minlength?: number;
    maxLength?: number;
    placeholder?: string;
    className?: string;
    touched?: boolean;
    externalUpdateValue?: string; // to update error messages when some related to Input external value changed
    autoComplete?: boolean | string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    triggerClear?: boolean;
    onChange?: (event: React.FormEvent, isValid: boolean) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    errorHandler?: (value: string) => string;
    checkShownErrors?: (name: string, errorMessage: string) => void;
}

interface IInputState {
    value: string;
    errorMessage: string;
    touched: boolean;
    required: boolean;
    externalUpdateValue: string;
}

export const createInput = (Component: React.ComponentType<IInputProps>) => {
    class TextField extends React.Component<IInputHOCProps, IInputState> {
        timeoutId: number;

        constructor(props: IInputHOCProps) {
            super(props);

            this.state = {
                value: '',
                errorMessage: '',
                touched: false,
                required: false,
                externalUpdateValue: '',
            };
        }

        static getDerivedStateFromProps(nextProps: IInputHOCProps, prevState: IInputState) {
            if (nextProps.value !== undefined && nextProps.value !== prevState.value) {
                return {
                    value: nextProps.value,
                    touched: nextProps.mask ? prevState.touched : nextProps.touched,
                    required: nextProps.required,
                };
            }

            if (nextProps.required !== prevState.required) {
                return {
                    required: nextProps.required,
                };
            }

            if (nextProps.touched) {
                return {
                    touched: nextProps.touched,
                };
            }

            if (
                nextProps.externalUpdateValue !== undefined &&
                nextProps.externalUpdateValue !== prevState.externalUpdateValue
            ) {
                return {
                    externalUpdateValue: nextProps.externalUpdateValue,
                };
            }

            return null;
        }

        componentDidMount() {
            this.checkErrors(this.state.value);
        }

        componentDidUpdate(prevProps: IInputHOCProps) {
            if (!prevProps.triggerClear && this.props.triggerClear) {
                clearTimeout(this.timeoutId);
                this.setState({
                    value: '',
                    touched: false,
                });
            } else if (
                this.props.touched !== prevProps.touched ||
                this.props.disabled !== prevProps.disabled ||
                this.props.required !== prevProps.required ||
                this.props.intl.locale !== prevProps.intl.locale ||
                (this.state.touched && prevProps.externalUpdateValue !== this.props.externalUpdateValue)
            ) {
                this.checkErrors(this.state.value);
            }
        }

        componentWillUnmount() {
            clearTimeout(this.timeoutId);
        }

        errorHandler = (value: string) => {
            if (this.props.required && (!value || !String(value).trim())) {
                return this.props.intl.formatMessage({ id: 'REQUIRED_FIELD' });
            } else if (this.props.minlength && value.length < this.props.minlength) {
                return this.props.intl.formatMessage({ id: 'MIN_@LENGTH@' }, { minLength: this.props.minlength });
            } else if (this.props.maxLength && value.length > this.props.maxLength) {
                return this.props.intl.formatMessage({ id: 'MAX_@LENGTH@' }, { maxLength: this.props.maxLength });
            } else if (this.props.errorHandler) {
                return this.props.errorHandler(value);
            }
            return '';
        };

        checkErrors = (value: string) => {
            const errorMessage = this.errorHandler(value);

            this.setState((state) => {
                return {
                    errorMessage,
                    touched: state.touched || this.props.touched,
                };
            });

            if (this.props.checkShownErrors) {
                this.props.checkShownErrors(this.props.name, errorMessage);
            }
        };

        onChange = (event: React.FormEvent) => {
            const input = event.target as HTMLInputElement | HTMLTextAreaElement;
            const errorMessage = this.errorHandler(input.value);

            if (this.props.numeric && input.value !== '' && !Number(input.value)) return;

            this.setState({
                errorMessage,
                value: input.value,
            });

            if (this.props.onChange) {
                this.props.onChange(event, !errorMessage);
            }
        };

        onBlur = (event: React.FormEvent) => {
            const input = event.target as HTMLInputElement | HTMLTextAreaElement;
            const value = input.value;

            this.timeoutId = window.setTimeout(() => {
                this.setState({ touched: true }, () => this.checkErrors(value));
            }, 100);
        };

        render() {
            const placeholder = this.props.placeholder
                ? this.props.intl.formatMessage({ id: this.props.placeholder })
                : '';
            const autoComplete = this.props.autoComplete
                ? this.props.autoComplete === 'new-password'
                    ? 'new-password'
                    : 'on'
                : 'off';

            return (
                <>
                    <Component
                        autoFocus={this.props.autofocus}
                        name={this.props.name}
                        type={this.props.type || 'text'}
                        value={this.state.value}
                        disabled={this.props.disabled || false}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        className={genarateInputClasses(
                            this.props.disabled,
                            this.state.errorMessage && this.state.touched,
                            this.props.className
                        )}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                        onKeyDown={this.props.onKeyDown}
                        {...(this.props.maxLength && { maxLength: this.props.maxLength })}
                        {...(this.props.mask && { mask: this.props.mask })}
                        {...(this.props.maskChar && { maskChar: this.props.maskChar })}
                        {...(this.props.ariaLabel && {
                            'aria-label': this.props.intl.formatMessage({ id: this.props.ariaLabel }),
                        })}
                        {...(this.props.ariaLabelledBy && { 'aria-labelledby': this.props.ariaLabelledBy })}
                    />
                    {this.state.errorMessage && this.state.touched && !this.props.disabled && (
                        <InputError>{this.state.errorMessage}</InputError>
                    )}
                </>
            );
        }
    }

    return injectIntl(TextField);
};

// helper functions
const genarateInputClasses = (disabled = false, error = false, className = ''): string => {
    const classNames = [styles.input];

    if (disabled) classNames.push(styles.disabled);
    if (error && !disabled) classNames.push(styles.error);
    if (className) classNames.push(className);

    return classNames.join(' ');
};
